import React, { useMemo, useEffect, useRef, useCallback } from "react";
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  LineSeries,
  Category,
  Tooltip,
  Inject,
  Legend,
  Zoom,
  Crosshair,
} from "@syncfusion/ej2-react-charts";
import { formatNumber } from "../../utils/formatNumber";
import {
  MINUTE_ZOOM_SETTINGS,
  NO_CROSSHAIR,
  NO_ZOOM_SETTINGS,
  minuteCrosshair,
  minuteLabel,
  useMinuteAxis,
} from "./minuteAxis";

const COLORS = {
  tuneIn: "#1976d2",
  tuneOut: "#dc2626",
};

export default function TuneInOutChart({
  tuneIn = [],
  tuneOut = [],
  mode = "minute",
  refreshKey,
}) {
  const chartRef = useRef(null);

  useEffect(() => {
    chartRef.current?.refresh();
  }, [refreshKey]);

  const storedUser = JSON.parse(localStorage.getItem("adm_panel_user") || "{}");
  const tickColor = storedUser.darkmode ? "#64748b" : "#9ca3af";

  const {
    ref: wrapRef,
    range: minuteAxis,
    onZoomComplete: handleZoomComplete,
    axisProps: minuteAxisProps,
  } = useMinuteAxis({ series: tuneIn, mode, tickColor });

  /* =========================
     NORMALIZE DATA WITH INDEX ENCODING
     For non-minute modes, encode index to preserve sort order
  ========================= */
  const { chartDataIn, chartDataOut, yAxisMax } = useMemo(() => {
    const safeIn = Array.isArray(tuneIn) ? tuneIn : [];
    const safeOut = Array.isArray(tuneOut) ? tuneOut : [];

    const xOf = (p, i) => {
      if (minuteAxis) return p.index;
      return mode === "minute" ? p.label : `${p.label}__${i}`;
    };

    let in_data = safeIn.map((p, i) => ({
      x: xOf(p, i),
      y: Number(p.value) || 0,
    }));

    let out_data = safeOut.map((p, i) => ({
      x: xOf(p, i),
      y: Number(p.value) || 0,
    }));

    const maxIn = Math.max(...in_data.map((d) => d.y), 0);
    const maxOut = Math.max(...out_data.map((d) => d.y), 0);
    const max = Math.max(maxIn, maxOut);

    return {
      chartDataIn: in_data,
      chartDataOut: out_data,
      yAxisMax: max > 0 ? Math.ceil(max * 1.1) : 10,
    };
  }, [tuneIn, tuneOut, mode, minuteAxis]);

  /* =========================
     DECODE CATEGORY LABEL
  ========================= */
  const decode = useCallback((v) => {
    const raw = String(v ?? "");
    if (mode === "minute") return { label: raw, idx: 0 };
    const [label, idx] = raw.split("__");
    return { label, idx: Number(idx) };
  }, [mode]);

  /* =========================
     TOOLTIP
  ========================= */
  const tooltipRender = useCallback(
    (args) => {
      if (!args.point) return;
      const label = minuteAxis
        ? minuteLabel(Number(args.point.x))
        : decode(args.point.x).label;
      args.text = `${label}<br/>${args.series.name} : ${formatNumber(args.point.y)}`;
    },
    [decode, minuteAxis]
  );

  const sharedTooltipRender = useCallback((args) => {
    const points = args.point ?? [];
    if (points.length === 0) return;
    args.headerText = minuteLabel(Number(points[0].x));
    args.text = points.map(
      (p, i) => `${args.series?.[i]?.name ?? ""} : ${formatNumber(p.y)}`
    );
  }, []);

  /* =========================
     AXIS LABEL RENDER
  ========================= */
  const axisLabelRender = useCallback(
    (args) => {
      // Y axis - format with thousands separator
      if (args.axis?.name === "primaryYAxis") {
        const value = parseFloat(args.value);
        if (!isNaN(value)) {
          args.text = formatNumber(value);
        }
        return;
      }

      // X axis - sparse label rendering
      if (args.axis?.name !== "primaryXAxis") return;

      // Every tick the axis emits is already a clock minute.
      if (minuteAxis) {
        args.text = minuteLabel(Number(args.value));
        return;
      }

      const { label, idx } = decode(args.text);

      if (mode === "minute") {
        // Show every hour
        const [h, m] = label.split(":").map(Number);
        const minuteIdx = h * 60 + m;
        if (minuteIdx % 60 !== 0) {
          args.text = "";
          return;
        }
        args.text = label;
        return;
      }

      if (mode === "hour") {
        // Show every 6th hour for day ranges
        if (idx % 6 !== 0) {
          args.text = "";
          return;
        }
        args.text = label;
        return;
      }

      if (mode === "day" || mode === "week" || mode === "month") {
        // Show all labels for these modes
        args.text = label;
        return;
      }

      args.text = label;
    },
    [mode, decode, minuteAxis]
  );

  const showMarkers = mode === "day" || mode === "week" || mode === "month";

  return (
    <div className="chart-container" ref={wrapRef}>
      <h3 className="chart-title">Tune-in / Tune-out</h3>

      <ChartComponent
        ref={chartRef}
        // Syncfusion throws if zoomSettings changes as a property, so the
        // axis kind remounts instead.
        key={minuteAxis ? "clock-axis" : "plain-axis"}
        height="420px"
        chartArea={{ border: { width: 0 } }}
        legendSettings={{ visible: true, position: "Bottom" }}
        primaryXAxis={{
          name: "primaryXAxis",
          valueType: "Category",
          interval: 1,
          labelIntersectAction: "Rotate45",
          edgeLabelPlacement: "Shift",
          majorGridLines: { width: 0 },
          labelPlacement: "OnTicks",
          ...(minuteAxisProps || {}),
        }}
        primaryYAxis={{
          minimum: 0,
          maximum: yAxisMax,
          majorTickLines: { width: 0 },
          lineStyle: { width: 0 },
        }}
        tooltip={{ enable: true, shared: !!minuteAxis, header: "" }}
        crosshair={minuteAxis ? minuteCrosshair(tickColor) : NO_CROSSHAIR}
        zoomSettings={minuteAxis ? MINUTE_ZOOM_SETTINGS : NO_ZOOM_SETTINGS}
        zoomComplete={handleZoomComplete}
        tooltipRender={tooltipRender}
        sharedTooltipRender={sharedTooltipRender}
        axisLabelRender={axisLabelRender}
      >
        <Inject services={[LineSeries, Category, Tooltip, Legend, Zoom, Crosshair]} />

        <SeriesCollectionDirective>
          <SeriesDirective
            dataSource={chartDataOut}
            xName="x"
            yName="y"
            type="Line"
            name="Tune-out"
            width={2}
            fill={COLORS.tuneOut}
            border={{ color: COLORS.tuneOut, width: 2 }}
            marker={{ visible: showMarkers }}
            animation={{ enable: false }}
          />

          <SeriesDirective
            dataSource={chartDataIn}
            xName="x"
            yName="y"
            type="Line"
            name="Tune-in"
            width={2}
            fill={COLORS.tuneIn}
            border={{ color: COLORS.tuneIn, width: 2 }}
            marker={{ visible: showMarkers }}
            animation={{ enable: false }}
          />
        </SeriesCollectionDirective>
      </ChartComponent>
    </div>
  );
}